import { invariant } from '@epic-web/invariant'
import { type LoaderFunctionArgs } from '@remix-run/node'
import {
	Form,
	json,
	useFetcher,
	useLoaderData,
	useRevalidator,
	useRouteLoaderData,
} from '@remix-run/react'
import jwt from '@tsndr/cloudflare-worker-jwt'
import { useLiveQuery } from 'dexie-react-hooks'
import {
	Ellipsis,
	GitCompare,
	HandHelping,
	NotebookIcon,
	StarsIcon,
	TargetIcon,
	TerminalIcon,
	Trash2Icon,
	TriangleAlertIcon,
} from 'lucide-react'
import usePartySocket from 'partysocket/react'
import { useState, useEffect, useRef } from 'react'
import Joyride, { STATUS, type Step } from 'react-joyride'
import { ClientOnly } from 'remix-utils/client-only'
import { toast } from 'sonner'
import { MonitorFeatureListItem } from '#app/components/monitor-feature-list-item.js'
import { Button } from '#app/components/ui/button.js'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '#app/components/ui/dropdown-menu.js'
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table'
import { Textarea } from '#app/components/ui/textarea.js'
import { type loader as targetLoader } from '#app/routes/targets+/$targetId+/route.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.js'
import { useWakeLock } from '#app/utils/hooks/use-wake-lock'
import { cn } from '#app/utils/misc.tsx'
import { TargetLiveEventUnion } from '#app/utils/partykit-events.ts'
import localScribeDb, { type ITranscriptLog } from '#app/utils/scribe-db'
import { getActiveTargetSession } from '#app/utils/target-session.server.js'
import { action as enqueueChunkProcessAction } from './__enqueue-chunk-process.server.tsx'
import { Recorder } from './recorder.tsx'

type TranscriptChunk = Omit<ITranscriptLog, 'id' | 'targetId'>

export const action = enqueueChunkProcessAction

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
	const { targetId } = params
	const userId = await getUserId(request)
	invariant(targetId, 'Target ID is required')

	// Creating a token
	const token = await jwt.sign(
		{
			userId,
			targetId,
			nbf: Math.floor(Date.now() / 1000), // Not before: Now
			exp: Math.floor(Date.now() / 1000) + 5, // Expires: Now + 5 seconds
		},
		process.env.JWT_SIGNING_SECRET,
	)

	const targetSessions = userId
		? await getActiveTargetSession(targetId, userId)
		: []

	const targetReveals = userId
		? await prisma.targetReveal.findMany({
				where: {
					targetId,
					userId,
				},
			})
		: []

	return json({
		targetId,
		userId,
		token,
		targetSessions,
		targetReveals,
	})
}

export default function ScribeIndex() {
	const { targetId, userId, token } = useLoaderData<typeof loader>()
	const targetLoaderData = useRouteLoaderData<typeof targetLoader>(
		'routes/targets+/$targetId+/route',
	)

	const anonId = useRef<string>(`anon-${crypto.randomUUID()}`)

	const [timestampStatusMap, setTimestampStatusMap] = useState<
		Record<number, 'pending' | 'completed'>
	>({})

	const [isProcessing, setIsProcessing] = useState(false)

	const [error, setError] = useState<string | null>(null)

	const [localSessionStatus, setLocalSessionStatus] = useState<
		'active' | 'inactive'
	>('inactive')

	const [runTour, setRunTour] = useState(false)

	useEffect(function handleFirstVisitTour() {
		const checkFirstVisit = async () => {
			const visited = localStorage.getItem(
				'intuizen_lastVisitedFeedbackLoopVersion',
			)
			if (!visited) {
				setRunTour(true)
				localStorage.setItem('intuizen_lastVisitedFeedbackLoopVersion', '1')
			}
		}

		checkFirstVisit()
	}, [])

	const revalidator = useRevalidator()

	const { requestWakeLock, releaseWakeLock } = useWakeLock()

	usePartySocket({
		query: {
			token,
		},
		host: ENV.PARTYKIT_URL,
		party: 'target',
		room: `${userId || anonId.current}:${targetId}`,
		onError(event) {
			revalidator.revalidate()
			toast.error('Reconnecting to signal line....', {
				icon: <TriangleAlertIcon className="w-4 text-red-500" />,
				duration: undefined,
				position: 'bottom-center',
				dismissible: false,
				description:
					'Wait to reconnect automatically or refresh the page and try again.',
				descriptionClassName: 'text-red-500',
				id: 'realtime-server-error',
			})
			setError(
				'Error occurred connecting to signal line. Please wait to reconnect, or refresh your page',
			)
		},
		onClose(event) {
			console.log('Closing party:', event)
		},
		onOpen(event) {
			console.log('Opening party:', event)
			setError(null)
			toast.dismiss('realtime-server-error')
		},
		onMessage(event) {
			let parsed = TargetLiveEventUnion.safeParse(JSON.parse(event.data))

			if (parsed.success) {
				if (parsed.data.type === 'transcript/chunk-processing-started') {
					setTimestampStatusMap(prev => ({
						...prev,
						[parsed.data.timestamp]: 'pending',
					}))
					setIsProcessing(true)
				}

				if (parsed.data.type === 'transcript/chunk-processing-completed') {
					setTimestampStatusMap(prev => ({
						...prev,
						[parsed.data.timestamp]: 'completed',
					}))
					setIsProcessing(false)
					localScribeDb.updateTranscriptRecord(
						parsed.data.timestamp,
						parsed.data.analysis,
						parsed.data.similarities,
					)

					// Provide feedback
					if (parsed.data.similarities.length > 0) {
						toast.success('Hit the Target!', {
							position: 'bottom-center',
							description: `${parsed.data.similarities.length} similarities found`,
							icon: <TargetIcon className="w-4 text-green-600" />,
						})
						new Audio('/sounds/arrow_hitting_target.mp3')
							.play()
							.catch(error => console.error('Error playing sound:', error))
					}
				}
			} else {
				console.error('Error parsing event data:', parsed.error.issues)
				toast.error('Error parsing event data', { position: 'top-center' })
			}
		},
	})

	const [transcriptChunks, setTranscriptChunks] = useState<TranscriptChunk[]>(
		[],
	)

	const transcriptLogs = useLiveQuery(
		() => localScribeDb.transcriptLog.where({ targetId }).sortBy('startedAt'),
		[targetId],
	)

	useEffect(
		function hydrateTranscriptChunks() {
			if (transcriptLogs) {
				setTranscriptChunks(transcriptLogs)
			}
		},
		[transcriptLogs],
	)

	const [chunkStartedAt, setChunkStartedAt] = useState<number>(Date.now())
	const [currentChunk, setCurrentChunk] = useState<string>('')

	const enqueueChunkProcess = useFetcher()

	const triggerEnqueueChunkProcess = async (
		chunk: string,
		timestamp: number,
	) => {
		enqueueChunkProcess.submit(
			{
				text: chunk,
				targetId,
				timestamp,
				userId: userId || anonId.current,
			},
			{
				method: 'post',
			},
		)
		localScribeDb.logTranscript({
			id: timestamp,
			targetId,
			startedAt: timestamp,
			text: chunk,
		})
		setIsProcessing(true)
	}

	const handleTranscriptChange = (
		interimTranscript: string,
		finalTranscript: string,
	) => {
		const currentTimestamp = Date.now()

		if (currentChunk.length > 0 && interimTranscript.length == 0) {
			// A new chunk has started
			setChunkStartedAt(currentTimestamp)
			setTranscriptChunks(prev => [
				...prev,
				{
					text: currentChunk,
					startedAt: chunkStartedAt,
					endedAt: currentTimestamp,
				},
			])

			triggerEnqueueChunkProcess(currentChunk, currentTimestamp)

			setCurrentChunk('')
		} else {
			setCurrentChunk(interimTranscript)
		}
	}

	const handleReset = () => {
		const confirm = window.confirm(
			'Are you sure you want to reset the session? This will clear all in-session and previously saved transcript logs.',
		)
		if (!confirm) {
			return
		}

		setTranscriptChunks([])
		setChunkStartedAt(Date.now())
		setCurrentChunk('')
		localScribeDb.clearTranscriptLogsForTarget(targetId)
		setLocalSessionStatus('inactive')
		releaseWakeLock()
	}

	const handleTextChunkSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (error) {
			return
		}
		if (currentChunk.trim()) {
			if (localSessionStatus === 'inactive') {
				setLocalSessionStatus('active')
			}
			const currentTimestamp = Date.now()
			setTranscriptChunks(prev => [
				...prev,
				{
					text: currentChunk.trim(),
					startedAt: currentTimestamp,
					endedAt: currentTimestamp,
				},
			])
			triggerEnqueueChunkProcess(currentChunk, currentTimestamp)
			setCurrentChunk('')
		} else {
			toast.error(
				'Please enter some text before submitting or start speech recorder.',
				{
					position: 'bottom-center',
				},
			)
		}
	}

	const steps: Step[] = [
		{
			title: 'Get Ready',
			target: '#target-card', // Assuming this class is added to the session status button
			content: `Use extra-sensory perception to retrieve information about this practice target.`,
			disableBeacon: true,
			isFixed: true,
		},
		{
			target: '.impressions-input', // Assuming this class is added to the impressions input section
			content:
				'Turn on the Speech Recorder to begin or type your impressions here.',
		},
		{
			target: '#monitor', // Assuming this class is added to the monitor button
			content:
				'Impressions will be processed by an AI in realtime so you can get feedback instantly.',
		},
		{
			target: '.logbook', // Assuming this class is added to the logbook
			content:
				"All you're impressions and monitor logs will be listed in your Session Logbook",
		},
		{
			target: '#session-status-button',
			content:
				'Once you have finished with recording your impressions, reveal the target to see an instant analysis.',
		},
	]

	return (
		<div className="mx-auto">
			<Joyride
				styles={{
					options: {
						primaryColor: 'black',
					},
				}}
				scrollOffset={64}
				steps={steps}
				run={runTour}
				continuous
				showProgress
				showSkipButton
				callback={data => {
					if (
						data.status === STATUS.FINISHED ||
						data.status === STATUS.SKIPPED ||
						data.action === 'close'
					) {
						setRunTour(false)
					}
				}}
			/>
			<div className="mb-4 flex items-center gap-2 rounded-lg border  bg-gray-100 p-4 text-foreground">
				<div className="flex items-center gap-8 font-bold">
					<h1 className="text-xl md:text-3xl">Session </h1>
					<Form
						action={
							userId ? `/targets/${targetId}` : `/targets/${targetId}/analysis`
						}
						method={userId ? 'post' : 'get'}
					>
						<input
							type="hidden"
							name="anonymous"
							value={!userId ? 'true' : 'false'}
						/>
						<Button
							type="submit"
							id="session-status-button"
							disabled={localSessionStatus === 'inactive'}
							variant={
								localSessionStatus === 'inactive' ? 'default' : 'default'
							}
							size="sm"
						>
							{localSessionStatus === 'inactive' ? (
								<>Ready</>
							) : localSessionStatus === 'active' ? (
								<>Reveal</>
							) : (
								<>
									<TerminalIcon className="mr-2 w-4" /> Completed
								</>
							)}
						</Button>
					</Form>
					<DropdownMenu>
						<DropdownMenuTrigger>
							<Button variant={'outline'}>
								<Ellipsis className="w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem onClick={() => setRunTour(true)}>
								<HandHelping className="mr-2 w-4" /> Tour
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
			<div className="grid grid-cols-1 gap-8 md:grid-cols-2">
				<div className="flex flex-col gap-4">
					<main className="rounded-lg border  p-2">
						<h2 className="text-md font-semibold">Preparation</h2>
						{/* <hr className="my-2" /> */}
						<p className="text-sm">
							Bring-your-own remote viewing preparation protocol. When ready to
							capture impressions, use the controls below.
						</p>
					</main>
					<main className="impressions-input rounded-lg border bg-gray-100 p-2 shadow-lg">
						<h2 className="text-xl font-semibold">Impressions Input</h2>
						<hr className="my-2" />

						<ClientOnly fallback={<div className="h-10">Loading...</div>}>
							{() => (
								<Recorder
									onListeningStart={() => {
										if (localSessionStatus === 'inactive') {
											setLocalSessionStatus('active')
											requestWakeLock()
										}
									}}
									onListeningStop={() => {
										setLocalSessionStatus('inactive')
										releaseWakeLock()
									}}
									onReset={handleReset}
									onTranscriptChange={handleTranscriptChange}
								/>
							)}
						</ClientOnly>
						<form onSubmit={handleTextChunkSubmit} className="mt-4">
							<Textarea
								value={currentChunk}
								onChange={e => setCurrentChunk(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault()
										handleTextChunkSubmit(e)
									}
								}}
								placeholder="Type your intuizen impressions..."
								className="w-full rounded border border-brand-primary-6 p-4 text-lg placeholder:text-gray-500 dark:text-brand-primary-10"
							/>
							<div className="flex items-center">
								<Button
									type="submit"
									variant={'default'}
									className="mt-2 w-full"
								>
									Submit
								</Button>
							</div>
						</form>
					</main>
					<div
						id="monitor"
						className="impressions-input rounded-lg border bg-gray-100 p-2"
					>
						<h2 className="text-xl font-semibold">
							<StarsIcon className="mr-2 inline-block w-6" /> Monitor Agent
						</h2>
						<hr className="my-2" />
						<MonitorFeatureListItem
							status={
								error ? 'offline' : isProcessing ? 'processing' : 'online'
							}
							title="Feedback Loop"
							description="Real-time evaluation of your impressions."
							icon={<GitCompare />}
						/>
					</div>
				</div>
				<aside className="grid gap-8">
					<Table className="border">
						<TableCaption>
							This is your session logbook. <br />
							Impressions will be recorded here.
						</TableCaption>
						<TableHeader className="logbook">
							<TableRow>
								<TableHead className="flex items-center justify-between bg-gray-100 text-lg font-bold">
									<span>
										<NotebookIcon className="mr-2 inline-block w-6" /> Logbook
									</span>
									<Button
										disabled={transcriptChunks.length == 0}
										variant={'outline'} // Assuming this class is added to the finish button
										size={'icon'}
										onClick={handleReset}
									>
										<Trash2Icon />
									</Button>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{transcriptChunks
								.sort((a, b) => b.startedAt - a.startedAt)
								.map((chunk, index) => (
									<TableRow className="group" key={index}>
										<TableCell className="relative bg-white">
											<span
												className={cn(
													timestampStatusMap[chunk.startedAt] === 'pending'
														? 'animate animate-pulse'
														: '',
												)}
											>
												{chunk.text}{' '}
												{!targetLoaderData?.isRevealed
													? chunk.similarities?.map(_similarity => '🎯')
													: ''}
											</span>
											<p className=" absolute bottom-0 right-0 text-gray-400 opacity-0 group-hover:opacity-100">
												{new Date(chunk.startedAt).toLocaleTimeString()}
											</p>
											{targetLoaderData?.isRevealed && (
												<div className="flex flex-wrap gap-2 italic">
													{chunk.similarities?.map(similarity => (
														<div key={similarity}>🎯 {similarity}</div>
													))}
												</div>
											)}
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</aside>
			</div>
		</div>
	)
}
